import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar, XAxis, YAxis,
  Tooltip, CartesianGrid, Legend,
} from 'recharts';
import {
  Shield, Key, AlertTriangle, Activity, RefreshCw, Search,
  Trash2, ChevronDown, Clock, Ban, TrendingUp,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CFG = {
  active: { label: 'Aktiv', cls: 'bg-emerald-100 text-emerald-700' },
  expiring_soon: { label: 'Läuft bald ab', cls: 'bg-amber-100 text-amber-700' },
  expired: { label: 'Abgelaufen', cls: 'bg-slate-100 text-slate-600' },
  revoked: { label: 'Widerrufen', cls: 'bg-red-100 text-red-700' },
  compromised: { label: 'Kompromittiert', cls: 'bg-red-200 text-red-800' },
};

const daysUntil = (iso) => iso ? Math.ceil((new Date(iso) - new Date()) / 86400000) : null;

const KpiCard = ({ label, value, icon: Icon, tone = 'slate', sub = null, testid }) => {
  const tones = {
    slate: 'text-slate-600 bg-slate-100',
    orange: 'text-orange-600 bg-orange-100',
    amber: 'text-amber-700 bg-amber-100',
    red: 'text-red-700 bg-red-100',
    emerald: 'text-emerald-700 bg-emerald-100',
  };
  return (
    <Card data-testid={testid}>
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${tones[tone]}`}><Icon className="h-5 w-5" /></div>
          <div className="flex-1 min-w-0">
            <p className="text-xs text-muted-foreground truncate">{label}</p>
            <p className="text-2xl font-semibold tabular-nums">{value}</p>
            {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default function ApiIntegrationsAdminTab() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [kpis, setKpis] = useState(null);
  const [trends, setTrends] = useState(null);
  const [tokens, setTokens] = useState([]);
  const [filters, setFilters] = useState({ status: 'all', search: '', expiring_within_days: '' });
  const [days, setDays] = useState(30);
  const [compromiseDialog, setCompromiseDialog] = useState(null);
  const [forceRotateDialog, setForceRotateDialog] = useState(null);

  const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  const load = useCallback(async () => {
    try {
      const q = new URLSearchParams();
      if (filters.status && filters.status !== 'all') q.append('status', filters.status);
      if (filters.search) q.append('search', filters.search);
      if (filters.expiring_within_days) q.append('expiring_within_days', filters.expiring_within_days);
      q.append('limit', '200');
      const [k, tr, tk] = await Promise.all([
        axios.get(`${API}/admin/api-integrations/kpis`, auth()),
        axios.get(`${API}/admin/api-integrations/usage-trends?days=${days}`, auth()),
        axios.get(`${API}/admin/api-integrations/tokens?${q.toString()}`, auth()),
      ]);
      setKpis(k.data);
      setTrends(tr.data);
      setTokens(tk.data.data || []);
    } catch (e) {
      toast.error(t('admin.common.loadError'));
    } finally {
      setLoading(false);
    }
  }, [filters.status, filters.search, filters.expiring_within_days, days]);

  useEffect(() => { load(); }, [load]);

  const handleCompromise = async () => {
    if (!compromiseDialog) return;
    try {
      await axios.post(
        `${API}/admin/api-integrations/tokens/${compromiseDialog.token_id}/compromise`,
        { reason: compromiseDialog.reason || '' },
        auth(),
      );
      toast.success(t('admin.apiInt.tokenCompromised'));
      setCompromiseDialog(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('admin.apiInt.actionFailed'));
    }
  };

  const handleForceRotate = async () => {
    if (!forceRotateDialog) return;
    const { token_ids, comment } = forceRotateDialog;
    if (!comment || comment.trim().length < 5) {
      toast.error(t('admin.apiInt.auditCommentRequired'));
      return;
    }
    try {
      const res = await axios.post(
        `${API}/admin/api-integrations/force-rotate`,
        { token_ids, comment },
        auth(),
      );
      toast.success(t('admin.apiInt.tokensRotated', { count: res.data.rotated?.length || 0 }));
      setForceRotateDialog(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('admin.apiInt.actionFailed'));
    }
  };

  if (loading) {
    return <div className="p-6 text-muted-foreground" data-testid="admin-api-loading">{t('admin.apiInt.loadingCockpit')}</div>;
  }

  const expiringTokens = tokens.filter(t => ['active', 'expiring_soon'].includes(t.status) && daysUntil(t.expires_at) !== null && daysUntil(t.expires_at) <= 14);

  return (
    <div className="space-y-6" data-testid="admin-api-integrations-tab">
      {/* KPI Row */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <KpiCard testid="kpi-sites-with-api" label="Sites mit API" value={kpis?.sites_with_api ?? 0} icon={Shield} tone="slate" />
        <KpiCard testid="kpi-active-tokens" label="Aktive Tokens" value={kpis?.active_tokens ?? 0} icon={Key} tone="emerald" />
        <KpiCard testid="kpi-expiring-soon" label="Ablauf < 14 T" value={kpis?.expiring_soon ?? 0} icon={Clock} tone="amber" />
        <KpiCard testid="kpi-revoked-30d" label={t('admin.apiInt.revoked') + ' (30d)'} value={kpis?.revoked_30d ?? 0} icon={Ban} tone="red" />
        <KpiCard testid="kpi-requests-30d" label={t('admin.apiInt.requests') + ' (30d)'} value={(kpis?.requests_30d ?? 0).toLocaleString()} icon={Activity} tone="orange" />
        <KpiCard testid="kpi-error-rate" label={t('admin.apiInt.errors') + ' (30d)'} value={`${kpis?.error_rate_30d ?? 0}%`} icon={AlertTriangle} tone={(kpis?.error_rate_30d ?? 0) > 5 ? 'red' : 'slate'} />
      </div>

      {/* Usage Trend Charts */}
      <Card data-testid="admin-api-usage-trend-card">
        <CardHeader className="flex-row items-center justify-between pb-3">
          <div>
            <CardTitle className="text-lg flex items-center gap-2"><TrendingUp className="h-5 w-5 text-orange-500" /> {t('admin.apiInt.usageTrends')}</CardTitle>
            <CardDescription>{t('admin.apiInt.requestsPerDay')}</CardDescription>
          </div>
          <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
            <SelectTrigger className="w-32" data-testid="admin-api-days-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="7">{t('admin.apiInt.days7')}</SelectItem>
              <SelectItem value="30">{t('admin.apiInt.days30')}</SelectItem>
              <SelectItem value="90">{t('admin.apiInt.days90')}</SelectItem>
            </SelectContent>
          </Select>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="h-64" data-testid="admin-api-requests-chart">
              <p className="text-xs text-muted-foreground mb-2">{t('admin.apiInt.requestsPerDay2')}</p>
              {(trends?.daily || []).length === 0 ? (
                <div className="h-full flex items-center justify-center text-sm text-muted-foreground border rounded-md border-dashed">
                  {t('admin.apiInt.noDataInRange')}
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={trends.daily}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="requests" stroke="#F97316" strokeWidth={2} dot={false} name={t('admin.apiInt.requests')} />
                    <Line type="monotone" dataKey="errors" stroke="#DC2626" strokeWidth={2} dot={false} name={t('admin.apiInt.errors')} />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
            <div className="h-64" data-testid="admin-api-top-sites-chart">
              <p className="text-xs text-muted-foreground mb-2">{t('admin.apiInt.topSites')}</p>
              {(trends?.top_sites || []).length === 0 ? (
                <div className="h-full flex items-center justify-center text-sm text-muted-foreground border rounded-md border-dashed">
                  {t('admin.common.noData')}
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={trends.top_sites} layout="vertical" margin={{ left: 60 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis type="number" tick={{ fontSize: 11 }} />
                    <YAxis type="category" dataKey="site_name" tick={{ fontSize: 11 }} width={110} />
                    <Tooltip />
                    <Bar dataKey="requests" fill="#F97316" />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tokens Table */}
      <Card data-testid="admin-api-tokens-card">
        <CardHeader className="flex-row items-center justify-between gap-3 flex-wrap pb-3">
          <div>
            <CardTitle className="text-lg flex items-center gap-2"><Key className="h-5 w-5 text-orange-500" /> {t('admin.apiInt.allTokens')}</CardTitle>
            <CardDescription>{t('admin.apiInt.filterableList')}</CardDescription>
          </div>
          <div className="flex gap-2 flex-wrap">
            <div className="relative">
              <Search className="h-4 w-4 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                data-testid="admin-api-search-input"
                placeholder={t('admin.apiInt.labelPrefix')}
                value={filters.search}
                onChange={(e) => setFilters(f => ({ ...f, search: e.target.value }))}
                className="pl-8 w-48 h-9"
              />
            </div>
            <Select value={filters.status} onValueChange={(v) => setFilters(f => ({ ...f, status: v }))}>
              <SelectTrigger className="w-40 h-9" data-testid="admin-api-status-filter"><SelectValue placeholder={t('admin.apiInt.status')} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('admin.common.allStatuses')}</SelectItem>
                <SelectItem value="active">{t('admin.apiInt.active')}</SelectItem>
                <SelectItem value="expiring_soon">{t('admin.apiInt.expiringSoon')}</SelectItem>
                <SelectItem value="expired">{t('admin.apiInt.expired')}</SelectItem>
                <SelectItem value="revoked">{t('admin.apiInt.revoked')}</SelectItem>
                <SelectItem value="compromised">{t('admin.apiInt.compromised')}</SelectItem>
              </SelectContent>
            </Select>
            <Button size="sm" variant="outline" onClick={load} data-testid="admin-api-reload-btn">
              <RefreshCw className="h-4 w-4" />
            </Button>
            {expiringTokens.length > 0 && (
              <Button
                size="sm"
                variant="outline"
                className="text-amber-700 border-amber-300"
                data-testid="admin-api-force-rotate-btn"
                onClick={() => setForceRotateDialog({ token_ids: expiringTokens.map(et => et.token_id), comment: '' })}
              >
                <RefreshCw className="h-4 w-4 mr-1" />
                {t('admin.apiInt.forceRotate')} ({expiringTokens.length})
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {tokens.length === 0 ? (
            <div className="text-center py-10 text-muted-foreground" data-testid="admin-api-tokens-empty">
              {t('admin.apiInt.noTokensFound')}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-muted-foreground border-b">
                    <th className="py-2 pr-3">{t('admin.apiInt.label')}</th>
                    <th className="py-2 pr-3">{t('admin.apiInt.location')}</th>
                    <th className="py-2 pr-3">{t('admin.apiInt.prefix')}</th>
                    <th className="py-2 pr-3">{t('admin.apiInt.status')}</th>
                    <th className="py-2 pr-3">{t('admin.apiInt.expires')}</th>
                    <th className="py-2 pr-3">{t('admin.apiInt.lastUsed')}</th>
                    <th className="py-2 pr-3">{t('admin.apiInt.requests')}</th>
                    <th className="py-2 pr-3">{t('admin.apiInt.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {tokens.map(tok => {
                    const cfg = STATUS_CFG[tok.status] || STATUS_CFG.active;
                    const d = daysUntil(tok.expires_at);
                    return (
                      <tr key={tok.token_id} className="border-b hover:bg-muted/30" data-testid={`admin-token-row-${tok.token_id}`}>
                        <td className="py-2 pr-3 font-medium">{tok.label}</td>
                        <td className="py-2 pr-3 text-xs">{tok.site_name || tok.site_id}</td>
                        <td className="py-2 pr-3 font-mono text-xs">{tok.token_prefix}</td>
                        <td className="py-2 pr-3"><Badge className={cfg.cls}>{cfg.label}</Badge></td>
                        <td className="py-2 pr-3 text-xs">
                          {tok.expires_at ? new Date(tok.expires_at).toLocaleDateString() : '—'}
                          {d !== null && d >= 0 && ['active', 'expiring_soon'].includes(tok.status) && (
                            <span className={`ml-1 ${d <= 14 ? 'text-amber-600' : 'text-muted-foreground'}`}>({d}d)</span>
                          )}
                        </td>
                        <td className="py-2 pr-3 text-xs">{tok.last_used_at ? new Date(tok.last_used_at).toLocaleDateString() : '—'}</td>
                        <td className="py-2 pr-3 tabular-nums text-xs">{tok.usage_count || 0}</td>
                        <td className="py-2 pr-3">
                          <div className="flex gap-1">
                            {!['revoked', 'compromised'].includes(tok.status) && (
                              <Button
                                size="sm" variant="outline" className="h-7 text-red-700"
                                data-testid={`admin-token-compromise-btn-${tok.token_id}`}
                                onClick={() => setCompromiseDialog({ token_id: tok.token_id, label: tok.label, site_name: tok.site_name, reason: '' })}
                              >
                                <AlertTriangle className="h-3.5 w-3.5 mr-1" />{t('admin.apiInt.compromised')}
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Compromise Dialog */}
      <Dialog open={!!compromiseDialog} onOpenChange={(o) => !o && setCompromiseDialog(null)}>
        <DialogContent data-testid="admin-api-compromise-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-red-600" /> {t('admin.apiInt.markCompromised')}</DialogTitle>
            <DialogDescription>
              <span dangerouslySetInnerHTML={{ __html: t('admin.apiInt.compromiseWarning') }} />
            </DialogDescription>
          </DialogHeader>
          {compromiseDialog && (
            <div className="space-y-3 py-2">
              <div className="text-sm">
                <p><span className="text-muted-foreground">{t('admin.apiInt.token')}:</span> <strong>{compromiseDialog.label}</strong></p>
                <p><span className="text-muted-foreground">{t('admin.apiInt.location')}:</span> {compromiseDialog.site_name}</p>
              </div>
              <div>
                <Label htmlFor="compromise-reason">{t('admin.common.reasonRequired')}</Label>
                <Textarea
                  id="compromise-reason"
                  data-testid="admin-api-compromise-reason"
                  rows={3}
                  value={compromiseDialog.reason}
                  onChange={(e) => setCompromiseDialog({ ...compromiseDialog, reason: e.target.value })}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCompromiseDialog(null)} data-testid="admin-api-compromise-cancel">{t('common.cancel')}</Button>
            <Button
              className="bg-red-600 hover:bg-red-700"
              onClick={handleCompromise}
              disabled={!compromiseDialog?.reason || compromiseDialog.reason.length < 5}
              data-testid="admin-api-compromise-confirm"
            >
              <AlertTriangle className="h-4 w-4 mr-2" />{t('admin.apiInt.mark')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Force Rotate Dialog */}
      <Dialog open={!!forceRotateDialog} onOpenChange={(o) => !o && setForceRotateDialog(null)}>
        <DialogContent data-testid="admin-api-force-rotate-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><RefreshCw className="h-5 w-5 text-amber-600" /> {t('admin.apiInt.globalRotation')}</DialogTitle>
            <DialogDescription>
              {t('admin.apiInt.rotationWarning', { count: forceRotateDialog?.token_ids.length || 0 })}
            </DialogDescription>
          </DialogHeader>
          {forceRotateDialog && (
            <div className="space-y-3 py-2">
              <div>
                <Label htmlFor="rotate-comment">{t('admin.apiInt.auditCommentLabel')}</Label>
                <Textarea
                  id="rotate-comment"
                  data-testid="admin-api-rotate-comment"
                  rows={3}
                  value={forceRotateDialog.comment}
                  onChange={(e) => setForceRotateDialog({ ...forceRotateDialog, comment: e.target.value })}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setForceRotateDialog(null)} data-testid="admin-api-rotate-cancel">{t('common.cancel')}</Button>
            <Button
              className="bg-amber-600 hover:bg-amber-700"
              onClick={handleForceRotate}
              data-testid="admin-api-rotate-confirm"
              disabled={(forceRotateDialog?.comment || '').trim().length < 5}
            >
              <RefreshCw className="h-4 w-4 mr-2" />{t('admin.apiInt.rotateNow')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
